/**
 * Comments Realtime Service
 * Handles real-time connection to Ably for comment updates
 */

import type { CommentPayload } from "@shared/types";
import * as Ably from "ably";
import { apiClient } from "@/lib/api-client";

export interface CommentsRealtimeConnection {
	disconnect: () => void;
}

export interface CreateRealtimeAuthTokenResponse {
	tokenRequest: Ably.TokenRequest;
	channel: string;
	clientId: string;
}

/**
 * Connect to Ably for real-time comment updates on a specific project
 */
export async function connectCommentsRealtime(options: {
	projectId: string;
	onComment: (comment: CommentPayload["comment"]) => void;
	onConnected?: () => void;
	onError?: (error: unknown) => void;
}): Promise<CommentsRealtimeConnection> {
	// Fetch the Ably token request from backend
	const initialAuth = await fetchCommentRealtimeToken(options.projectId);
	let nextAuth: CreateRealtimeAuthTokenResponse | null = initialAuth;

	const client = new Ably.Realtime({
		authCallback: async (_tokenParams, callback) => {
			try {
				const auth =
					nextAuth ?? (await fetchCommentRealtimeToken(options.projectId));
				nextAuth = null;
				callback(null, auth.tokenRequest);
			} catch (error) {
				options.onError?.(error);
				callback("Realtime auth failed", null);
			}
		},
	});

	const channel = client.channels.get(initialAuth.channel);
	const connectedListener = () => options.onConnected?.();
	const failedListener = (stateChange: unknown) =>
		options.onError?.(stateChange);

	client.connection.on("connected", connectedListener);
	client.connection.on("failed", failedListener);

	await channel.subscribe("comment.created", (message) => {
		const payload = message.data as CommentPayload;
		if (payload?.comment) {
			options.onComment(payload.comment);
		}
	});

	return {
		disconnect: () => {
			channel.unsubscribe("comment.created");
			client.connection.off("connected", connectedListener);
			client.connection.off("failed", failedListener);
			client.close();
		},
	};
}

/**
 * Fetch Ably token request from backend for comments
 */
async function fetchCommentRealtimeToken(
	projectId: string,
): Promise<CreateRealtimeAuthTokenResponse> {
	const response = await apiClient.post(
		`/api/v1/comments/realtime/auth?entityType=project&entityId=${projectId}`,
	);

	return response.data.data;
}
